import { useState, useCallback } from 'react';
import { hapticFeedback, showPopup } from '../lib/telegram';

export interface DeliveryLocation {
  latitude: number;
  longitude: number;
  address?: string;
}

interface UseDeliveryLocationReturn {
  deliveryLocation: DeliveryLocation | null;
  deliveryAddress: string;
  locationLoading: boolean;
  locationError: string | null;
  handleRequestLocation: () => void;
  setDeliveryAddress: (address: string) => void;
  setDeliveryLocation: (location: DeliveryLocation | null) => void;
}

export function useDeliveryLocation(): UseDeliveryLocationReturn {
  const [deliveryLocation, setDeliveryLocation] = useState<DeliveryLocation | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const handleRequestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Tu navegador no soporta geolocalización');
      showPopup(
        'Geolocalización no disponible',
        'Tu navegador no soporta geolocalización. Intenta abrir la app desde Telegram en tu teléfono.',
      );
      return;
    }

    setLocationLoading(true);
    setLocationError(null);
    hapticFeedback('impact', 'light');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDeliveryLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationLoading(false);
        setLocationError(null);
        hapticFeedback('notification', 'success');
      },
      async (error) => {
        setLocationLoading(false);
        hapticFeedback('notification', 'error');

        const retryButtons: { id: string; type: 'default'; text: string }[] = [
          { id: 'retry', type: 'default', text: 'Intentar de nuevo' },
          { id: 'cancel', type: 'default', text: 'Cancelar' },
        ];

        switch (error.code) {
          case error.PERMISSION_DENIED: {
            setLocationError('Permiso de ubicación denegado');
            const buttonId = await showPopup(
              'Ubicación requerida',
              'Activa los permisos de ubicación para Telegram en los ajustes de tu teléfono.',
              retryButtons,
            );
            if (buttonId === 'retry') {
              handleRequestLocation();
            }
            break;
          }
          case error.POSITION_UNAVAILABLE: {
            setLocationError('No se pudo obtener tu ubicación');
            const buttonId = await showPopup(
              'Ubicación no disponible',
              'Verifica que el GPS esté activado en tu teléfono.',
              retryButtons,
            );
            if (buttonId === 'retry') {
              handleRequestLocation();
            }
            break;
          }
          case error.TIMEOUT: {
            setLocationError('La solicitud de ubicación expiró');
            const buttonId = await showPopup(
              'Tiempo agotado',
              'No se pudo obtener tu ubicación a tiempo. Verifica tu señal GPS.',
              retryButtons,
            );
            if (buttonId === 'retry') {
              handleRequestLocation();
            }
            break;
          }
          default:
            setLocationError('Error al obtener ubicación');
            showPopup(
              'Error',
              'No se pudo obtener tu ubicación. Intenta más tarde.',
            );
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  return {
    deliveryLocation,
    deliveryAddress,
    locationLoading,
    locationError,
    handleRequestLocation,
    setDeliveryAddress,
    setDeliveryLocation,
  };
}

